/**
 * 시연 OCR 갤러리 — 레거시 DemoImageOcrGallery 핵심 기능 포팅
 */
import { drugService } from '@/entities/drug/api/drug.api';
import { demoOcrService } from '@/entities/demo/api/demo-ocr.api';
import { preprocessService } from '@/entities/ocr/api/preprocess.api';
import {
  OcrCropWarpUploader,
  type OcrCropWarpFileItem,
} from '@/shared/components/OcrCropWarpUploader';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { buildDemoOcrPostProcessPayload } from '@/pages/demo/utils/demoOcrPostProcess';
import {
  ensureDemoOcrRowKeys,
  extractOcrDataRows,
  pickFirst,
} from '@/pages/demo/utils/demoOcrResultModel';
import { readStoredBatchJobIds, writeStoredBatchJobIds } from '@/pages/demo/utils/demoDocumentAiBatchStorage';
import { getInitialOcrCornerPoints } from '@/shared/utils/ocrCropWarpUtils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Point2D } from '@/shared/utils/ocrImagePreprocess';
import { ToolbarRow } from '@/shared/components/ui/row';

const OCR_SOURCE_DOCUMENT_AI = 'document-ai-upload';
const OCR_SOURCE_BE_PREPROCESSED = 'be-preprocessed';

type OcrStatus = 'idle' | 'running' | 'done' | 'error';

interface OcrSourceState {
  ocrStatus: OcrStatus;
  ocrResult: unknown;
  ocrSnapshot: unknown;
  postProcessRequest: unknown;
  postProcessResponse: unknown;
}

interface GalleryItem {
  id: string;
  name: string;
  preview: string;
  file: File;
  ocrBySource: Partial<Record<string, OcrSourceState>>;
}

const createOcrSourceState = (): OcrSourceState => ({
  ocrStatus: 'idle',
  ocrResult: null,
  ocrSnapshot: null,
  postProcessRequest: null,
  postProcessResponse: null,
});

const getOcrSourceState = (item: GalleryItem, sourceKey: string): OcrSourceState => {
  const scoped = item.ocrBySource?.[sourceKey];
  return { ...createOcrSourceState(), ...scoped };
};

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png']);

const formatDash = (value: unknown) => {
  if (value == null || value === '') return '—';
  return String(value);
};

const formatNumber = (value: unknown) => {
  if (value == null || value === '') return '—';
  const n = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString() : '—';
};

const getPreviousMonthValue = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

export interface DemoImageOcrGalleryProps {
  prescriptionMonth?: string;
  enablePostProcess?: boolean;
  useTaskQueueOcrClient?: boolean;
}

export const DemoImageOcrGallery = ({
  prescriptionMonth: prescriptionMonthProp,
  enablePostProcess = false,
  useTaskQueueOcrClient = false,
}: DemoImageOcrGalleryProps) => {
  const toast = useToast();
  const [prescriptionMonth, setPrescriptionMonth] = useState(
    prescriptionMonthProp ?? getPreviousMonthValue(),
  );
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeOcrSource, setActiveOcrSource] = useState(OCR_SOURCE_DOCUMENT_AI);
  const [ocrSubmitting, setOcrSubmitting] = useState(false);
  const [postProcessSubmitting, setPostProcessSubmitting] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editedImages, setEditedImages] = useState<OcrCropWarpFileItem[]>([]);
  const [initialPoints, setInitialPoints] = useState<Point2D[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId],
  );

  const selectedOcrState = selectedItem
    ? getOcrSourceState(selectedItem, activeOcrSource)
    : createOcrSourceState();

  const ocrRows = useMemo(
    () => extractOcrDataRows(selectedOcrState.ocrResult),
    [selectedOcrState.ocrResult],
  );

  useEffect(() => {
    if (prescriptionMonthProp) setPrescriptionMonth(prescriptionMonthProp);
  }, [prescriptionMonthProp]);

  const patchItemSource = useCallback(
    (itemId: string, sourceKey: string, patch: Partial<OcrSourceState>) => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.id !== itemId) return item;
          const nextSource = { ...getOcrSourceState(item, sourceKey), ...patch };
          return {
            ...item,
            ocrBySource: { ...item.ocrBySource, [sourceKey]: nextSource },
          };
        }),
      );
    },
    [],
  );

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList?.length) return;
    const next: GalleryItem[] = [];
    for (const file of Array.from(fileList)) {
      if (!ALLOWED_TYPES.has(file.type)) {
        toast.error(`${file.name}: JPEG/PNG만 업로드할 수 있습니다.`);
        continue;
      }
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        preview: URL.createObjectURL(file),
        file,
        ocrBySource: {},
      });
    }
    if (!next.length) return;
    setItems((prev) => [...prev, ...next]);
    if (!selectedId) setSelectedId(next[0].id);
  };

  const runOcr = async () => {
    if (!selectedItem) return;
    setOcrSubmitting(true);
    patchItemSource(selectedItem.id, activeOcrSource, { ocrStatus: 'running' });
    try {
      const formData = new FormData();
      formData.append('file', selectedItem.file);
      const result =
        activeOcrSource === OCR_SOURCE_BE_PREPROCESSED
          ? await demoOcrService.requestPreprocessedOcr(formData)
          : await demoOcrService.processUpload(formData, useTaskQueueOcrClient);

      const keyed = ensureDemoOcrRowKeys(result, selectedItem.id);
      patchItemSource(selectedItem.id, activeOcrSource, {
        ocrStatus: 'done',
        ocrResult: keyed,
        ocrSnapshot: keyed,
        postProcessRequest: null,
        postProcessResponse: null,
      });
      toast.success('OCR이 완료되었습니다.');
    } catch (error) {
      patchItemSource(selectedItem.id, activeOcrSource, {
        ocrStatus: 'error',
        ocrResult: null,
      });
      handleApiError(error, toast);
    } finally {
      setOcrSubmitting(false);
    }
  };

  const runPostProcess = async () => {
    if (!selectedItem || !selectedOcrState.ocrResult) return;
    setPostProcessSubmitting(true);
    try {
      const payload = buildDemoOcrPostProcessPayload({
        ocrResult: selectedOcrState.ocrSnapshot ?? selectedOcrState.ocrResult,
        prescriptionYearMonth: prescriptionMonth.replace('-', ''),
      });
      const response = await demoOcrService.requestPostProcess(payload);
      patchItemSource(selectedItem.id, activeOcrSource, {
        postProcessRequest: payload,
        postProcessResponse: response,
      });
      toast.success('Post-process가 완료되었습니다.');
    } catch (error) {
      handleApiError(error, toast);
    } finally {
      setPostProcessSubmitting(false);
    }
  };

  const openEditor = async () => {
    if (!selectedItem) return;
    setEditorOpen(true);
    try {
      const corners = await preprocessService.getCornersFromUrl(selectedItem.preview);
      setInitialPoints(getInitialOcrCornerPoints(corners));
    } catch {
      setInitialPoints(null);
    }
    setEditedImages([
      {
        uid: selectedItem.id,
        name: selectedItem.name,
        status: 'done',
        type: selectedItem.file.type,
        size: selectedItem.file.size,
        preview: selectedItem.preview,
        originFileObj: selectedItem.file,
      },
    ]);
  };

  const lookupDrug = async (row: Record<string, unknown>) => {
    const refYm = prescriptionMonth.replace('-', '');
    const code = String(pickFirst(row, ['drugCode', 'code']) ?? '').trim();
    const name = String(pickFirst(row, ['drugName', 'name']) ?? '').trim();
    try {
      const result = code
        ? await drugService.searchDrugByCode(code, refYm)
        : name
          ? (await drugService.searchDrugsByName(name, refYm))?.[0]
          : null;
      if (!result) {
        toast.error('의약품을 찾지 못했습니다.');
        return;
      }
      toast.success(`${result.drugName ?? name} 매칭 완료`);
    } catch (error) {
      handleApiError(error, toast);
    }
  };

  const removeItem = (id: string) => {
    setItems((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.preview?.startsWith('blob:')) URL.revokeObjectURL(target.preview);
      return prev.filter((item) => item.id !== id);
    });
    if (selectedId === id) setSelectedId(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">이미지 업로드</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FileUploadButton
            inputRef={fileInputRef}
            accept="image/jpeg,image/png"
            multiple
            resetOnSelect={false}
            onChange={(event) => handleFiles(event.target.files)}
          >
            JPEG/PNG 이미지 추가
          </FileUploadButton>

          {items.length > 0 && (
            <ToolbarRow gap={8}>
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedId(item.id)}
                  className={`relative overflow-hidden rounded-md border-2 ${selectedId === item.id ? 'border-primary' : 'border-transparent'}`}
                >
                  <img src={item.preview} alt={item.name} className="h-20 w-20 object-cover" />
                </button>
              ))}
            </ToolbarRow>
          )}
        </CardContent>
      </Card>

      {selectedItem && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">{selectedItem.name}</CardTitle>
              <p className="text-sm text-muted-foreground">선택된 이미지 OCR 시연</p>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="prescription-month" className="whitespace-nowrap text-sm">
                처방 년월
              </Label>
              <Input
                id="prescription-month"
                type="month"
                value={prescriptionMonth}
                onChange={(e) => setPrescriptionMonth(e.target.value)}
                className="w-40"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={activeOcrSource === OCR_SOURCE_DOCUMENT_AI ? 'default' : 'outline'}
                onClick={() => setActiveOcrSource(OCR_SOURCE_DOCUMENT_AI)}
              >
                Document AI 직접 OCR
              </Button>
              <Button
                size="sm"
                variant={activeOcrSource === OCR_SOURCE_BE_PREPROCESSED ? 'default' : 'outline'}
                onClick={() => setActiveOcrSource(OCR_SOURCE_BE_PREPROCESSED)}
              >
                BE preprocess OCR
              </Button>
            </div>
            <div className="space-y-4">
                <ToolbarRow gap={8}>
                  <Button onClick={() => void runOcr()} disabled={ocrSubmitting}>
                    {ocrSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    OCR 실행
                  </Button>
                  <Button variant="outline" onClick={() => void openEditor()}>
                    이미지 편집 (crop/warp)
                  </Button>
                  {enablePostProcess && (
                    <Button
                      variant="secondary"
                      onClick={() => void runPostProcess()}
                      disabled={postProcessSubmitting || !selectedOcrState.ocrResult}
                    >
                      {postProcessSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Post-process
                    </Button>
                  )}
                  <Button variant="destructive" size="sm" onClick={() => removeItem(selectedItem.id)}>
                    삭제
                  </Button>
                  <Badge variant={selectedOcrState.ocrStatus === 'done' ? 'default' : 'secondary'}>
                    {selectedOcrState.ocrStatus}
                  </Badge>
                </ToolbarRow>

                <img
                  src={selectedItem.preview}
                  alt={selectedItem.name}
                  className="max-h-80 rounded-md border object-contain"
                />

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>매칭</TableHead>
                      <TableHead>코드</TableHead>
                      <TableHead>품명</TableHead>
                      <TableHead>단가</TableHead>
                      <TableHead>원내</TableHead>
                      <TableHead>원외</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ocrRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                          OCR 결과가 없습니다.
                        </TableCell>
                      </TableRow>
                    ) : (
                      ocrRows.map((row, idx) => (
                        <TableRow key={String(row.__demoRowKey ?? idx)}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Badge variant={row.isMatched ? 'default' : 'secondary'}>
                              {row.isMatched ? 'Y' : 'N'}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDash(row.drugCode ?? row.code)}</TableCell>
                          <TableCell>{formatDash(row.drugName ?? row.name)}</TableCell>
                          <TableCell>{formatNumber(row.unitPrice ?? row.price)}</TableCell>
                          <TableCell>
                            {formatNumber(row.inHospitalQuantity ?? row.inHouseUsageAmount)}
                          </TableCell>
                          <TableCell>
                            {formatNumber(row.outHospitalQuantity ?? row.outpatientUsageAmount)}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              className="ruda-button"
                              onClick={() => void lookupDrug(row)}
                            >
                              검색
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {enablePostProcess && selectedOcrState.postProcessResponse != null && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Post-process 응답</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="max-h-60 overflow-auto rounded bg-muted p-3 text-xs">
                        {JSON.stringify(selectedOcrState.postProcessResponse, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                )}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>OCR 이미지 편집</DialogTitle>
          </DialogHeader>
          <OcrCropWarpUploader
            value={editedImages}
            onChange={setEditedImages}
            maxCount={1}
            sourceImageUrl={selectedItem?.preview}
            sourceInitialPoints={initialPoints ?? undefined}
            sourceImageName={selectedItem?.name}
            allowFileSelect={false}
            showSourceButton
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// 배치 job localStorage 동기화 (갤러리에서 배치 OCR 사용 시)
export const syncDemoBatchJobIds = (ids: string[]) => {
  const stored = readStoredBatchJobIds();
  writeStoredBatchJobIds([...new Set([...stored, ...ids])]);
};
